import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, Output, ViewContainerRef } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { OpenConfirmationModalService } from '@vendasta/galaxy/confirmation-modal';
import { GalaxyEmptyStateModule } from '@vendasta/galaxy/empty-state';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { GalaxyColumnDef, GalaxyDataSource, GalaxyTableModule } from '@vendasta/galaxy/table';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom, map } from 'rxjs';

import { AiKnowledgeI18nModule } from '../../assets/i18n/ai-knowledge-i18n.module';
import { AiKnowledgeService } from '../../services/ai-knowledge.service';
import { KnowledgeBaseGrantSdkService } from '../knowledge-base-grant-sdk.service';
import {
  KnowledgeBaseCreateDialogComponent,
  KnowledgeBaseCreateDialogData,
  KnowledgeBaseCreateResult,
} from '../knowledge-base-create-dialog/knowledge-base-create-dialog.component';
import { KnowledgeBaseSharingDialogComponent } from '../knowledge-base-sharing-dialog/knowledge-base-sharing-dialog.component';
import {
  KnowledgeBaseSourcesDialogComponent,
  KnowledgeBaseSourcesDialogData,
  KnowledgeBaseSourcesResult,
} from '../knowledge-base-sources-dialog/knowledge-base-sources-dialog.component';
import { kbIdentityKey } from '../namespace.util';
import {
  KnowledgeBaseSharingDialogData,
  OWNED_SENTINEL,
  TableKnowledgeBase,
  TableKnowledgeSource,
} from '../types';

/**
 * The Knowledge Collections table view. Renders the `GalaxyDataSource` it's given inside a
 * `glxy-table-container` (search / paging / sort come from the container) and owns the base-level
 * actions (create / manage sources / share). Mutations persist (when `persist`) and emit deltas so the
 * page can update its row set and rebuild the datasource — the table itself never materializes the rows.
 */
@Component({
  selector: 'ai-knowledge-knowledge-base-table',
  imports: [
    CommonModule,
    MatButtonModule,
    MatDialogModule,
    GalaxyEmptyStateModule,
    GalaxyTableModule,
    MatIconModule,
    MatMenuModule,
    MatSortModule,
    MatTableModule,
    MatTooltipModule,
    TranslateModule,
    AiKnowledgeI18nModule,
  ],
  templateUrl: './knowledge-base-table.component.html',
  styleUrls: ['./knowledge-base-table.component.scss'],
})
export class KnowledgeBaseTableComponent {
  @Input({ required: true }) dataSource!: GalaxyDataSource<TableKnowledgeBase, unknown, unknown>;
  /** Owned sources available to add to a KB (used by the create / manage-sources dialogs) in canned mode. */
  @Input() sources: TableKnowledgeSource[] = [];
  /**
   * Lazily loads the full owned-source candidate list when a create / manage-sources dialog opens (server
   * mode). Deferred so the page load doesn't eagerly fetch every source just to populate dialogs that may
   * never open. Falls back to `sources` when absent (canned / storybook mode).
   */
  @Input() sourcesLoader?: () => Promise<TableKnowledgeSource[]>;
  @Input() editingEnabled = true;
  @Input() canShare = false;
  /** When false (storybook/@Input mode), skip the backing service calls and rely on local state only. */
  @Input() persist = true;
  /** Render Share as a top-level icon button in the row instead of (only) inside the overflow menu. */
  @Input() shareAsTopLevelAction = false;
  /** When true, clicking a KB name emits `openDetail` (navigate to the dedicated page) rather than opening the sources dialog. */
  @Input() openDetailOnRowClick = false;

  /** Emitted when a row asks to open the dedicated KB detail page (only when `openDetailOnRowClick`). */
  @Output() openDetail = new EventEmitter<TableKnowledgeBase>();
  /** A KB was created; the page prepends it and rebuilds the datasource. */
  @Output() kbCreated = new EventEmitter<TableKnowledgeBase>();
  /** A KB's source membership changed; the page updates that row and rebuilds the datasource. */
  @Output() kbSourcesChanged = new EventEmitter<{ kbKey: string; sourceIds: string[] }>();
  /** A KB was deleted; the page removes it and rebuilds the datasource. */
  @Output() kbDeleted = new EventEmitter<TableKnowledgeBase>();

  private readonly dialog = inject(MatDialog);
  // Sharing dialog opens via MatDialog (root overlay); pass this so the dialog's injector chains to this
  // component's (module) injector and can resolve KnowledgeBaseGrantSdkService + the per-app tokens.
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly knowledgeService = inject(AiKnowledgeService);
  private readonly grants = inject(KnowledgeBaseGrantSdkService);
  private readonly confirmModal = inject(OpenConfirmationModalService);
  private readonly snackbar = inject(SnackbarService);
  private readonly translate = inject(TranslateService);

  /**
   * Column metadata for the container (order, titles, sortability). The visible headers are translated
   * in the template via custom `*matHeaderCellDef` cells; these `title`s only back the sort menu / column
   * organizer (both off here) and the default header. Resolved reactively with `translate.stream` — its
   * first value comes from `get()`, which waits for the loader, so it never renders the raw key (unlike
   * `instant`), and it re-resolves on language change.
   *
   * Sorting is omitted: the table is server-paged and the list endpoint has no sort field, so a client
   * sort would only reorder the current page (misleading). Restore sortable once the API supports it.
   */
  private readonly columnDefs: ReadonlyArray<{ id: string; titleKey?: string }> = [
    { id: 'name', titleKey: 'KNOWLEDGE_SHARING.BASE_TABLE.COL_NAME' },
    { id: 'description', titleKey: 'KNOWLEDGE_SHARING.BASE_TABLE.COL_DESCRIPTION' },
    { id: 'sourceCount', titleKey: 'KNOWLEDGE_SHARING.BASE_TABLE.COL_SOURCES' },
    { id: 'lastModified', titleKey: 'KNOWLEDGE_SHARING.BASE_TABLE.COL_LAST_MODIFIED' },
    { id: 'actions' },
  ];

  readonly columns = toSignal(
    this.translate.stream(this.columnDefs.flatMap((c) => (c.titleKey ? [c.titleKey] : []))).pipe(
      map((dict: Record<string, string>): GalaxyColumnDef[] =>
        this.columnDefs.map((c) => ({ id: c.id, title: c.titleKey ? dict[c.titleKey] : '' })),
      ),
    ),
    { initialValue: this.columnDefs.map((c): GalaxyColumnDef => ({ id: c.id })) },
  );

  /** Stable row identity so re-emits from the datasource don't recreate rows (which would close an open row menu). */
  trackRow = (_: number, row: TableKnowledgeBase): string => row.kbKey;

  isShared(row: TableKnowledgeBase): boolean {
    return row.managedBy !== '' && row.managedBy !== OWNED_SENTINEL;
  }

  /** Whether the overflow menu would have any items — used to hide the trigger so it never opens empty (which flickers). */
  hasRowActions(row: TableKnowledgeBase): boolean {
    return (this.canShare && !this.shareAsTopLevelAction) || (this.editingEnabled && !this.isShared(row));
  }

  /** Drives the empty-state copy (no matches vs. nothing yet). */
  isFiltered(): boolean {
    return !!this.dataSource?.state.searchTerm;
  }

  private isSourceShared(ks: TableKnowledgeSource): boolean {
    return ks.managedBy !== '' && ks.managedBy !== OWNED_SENTINEL;
  }

  /** Row-name click: navigate to the dedicated detail page when wired, else fall back to the sources dialog. */
  onNameClick(kb: TableKnowledgeBase): void {
    if (this.openDetailOnRowClick) {
      this.openDetail.emit(kb);
      return;
    }
    void this.openSourcesDialog(kb);
  }

  /** The owned-source candidates for a dialog: lazily fetched in server mode, else the canned `sources`. */
  private async resolveCandidateSources(): Promise<TableKnowledgeSource[]> {
    return this.persist && this.sourcesLoader ? await this.sourcesLoader() : this.sources;
  }

  async openSourcesDialog(kb: TableKnowledgeBase): Promise<void> {
    const candidates = await this.resolveCandidateSources();
    const memberIds = new Set(kb.sourceIds);
    const currentSources = candidates.filter((ks) => memberIds.has(ks.id));
    const availableSources = this.isShared(kb)
      ? currentSources
      : candidates.filter((ks) => !this.isSourceShared(ks));
    const data: KnowledgeBaseSourcesDialogData = {
      kbId: kb.id,
      kbName: kb.name,
      kbDescription: kb.description,
      managedBy: this.isShared(kb) ? kb.managedBy : undefined,
      currentSources: currentSources.map((ks) => ({ id: ks.id, kind: ks.kind, name: ks.name, subtitle: ks.subtitle })),
      availableSources: availableSources.map((ks) => ({ id: ks.id, kind: ks.kind, name: ks.name, subtitle: ks.subtitle })),
    };
    // The dialog awaits this callback and only closes on success — surfacing errors and keeping the
    // selection on failure. We emit the membership delta after close so the page rebuilds its row set.
    data.onApply = async (result: KnowledgeBaseSourcesResult) => {
      if (this.persist) {
        await this.knowledgeService.updateKnowledgeBaseSources(
          kb.id,
          this.knowledgeService.buildNamespaceInterface(),
          result.sourceIds,
        );
      }
    };
    const ref = this.dialog.open(KnowledgeBaseSourcesDialogComponent, {
      data,
      width: '640px',
      maxWidth: '92vw',
      autoFocus: false,
    });
    ref.afterClosed().subscribe((result: KnowledgeBaseSourcesResult | undefined) => {
      if (!result) return;
      this.kbSourcesChanged.emit({ kbKey: kb.kbKey, sourceIds: [...result.sourceIds] });
    });
  }

  /**
   * Confirm + delete a KB. The BE blocks deletion of a shared KB (412 GRANT_HAS_DEPENDENTS); we surface
   * that as a specific message ("revoke its shares first") rather than a generic failure.
   */
  async onDelete(row: TableKnowledgeBase): Promise<void> {
    const confirmed = await firstValueFrom(
      this.confirmModal.openModal({
        type: 'warn',
        title: this.translate.instant('KNOWLEDGE_SHARING.BASE_TABLE.DELETE_TITLE'),
        message: this.translate.instant('KNOWLEDGE_SHARING.BASE_TABLE.DELETE_MESSAGE', { name: row.name }),
        confirmButtonText: this.translate.instant('COMMON.DELETE'),
      }),
    );
    if (!confirmed) return;
    const result = await this.grants.deleteKnowledgeBase(row.id);
    // `'reason' in result` is the union narrow ngc reliably honors across the await (plain `result.ok` doesn't).
    if (!('reason' in result)) {
      this.kbDeleted.emit(row);
      this.snackbar.openSuccessSnack(this.translate.instant('KNOWLEDGE_SHARING.BASE_TABLE.DELETE_SUCCESS'));
      return;
    }
    const message =
      result.reason === 'has-grants'
        ? this.translate.instant('KNOWLEDGE_SHARING.BASE_TABLE.DELETE_SHARED_ERROR', { name: row.name })
        : this.translate.instant('KNOWLEDGE_SHARING.BASE_TABLE.DELETE_ERROR');
    this.snackbar.openErrorSnack(message);
  }

  openShareDialog(row: TableKnowledgeBase): void {
    const data: KnowledgeBaseSharingDialogData = { kbId: row.id, kbName: row.name };
    this.dialog.open(KnowledgeBaseSharingDialogComponent, {
      data,
      width: '720px',
      maxWidth: '92vw',
      autoFocus: false,
      viewContainerRef: this.viewContainerRef,
    });
  }

  async openCreateKBDialog(): Promise<void> {
    const candidates = await this.resolveCandidateSources();
    const ownedKSes = candidates.filter((r) => !this.isSourceShared(r));
    const data: KnowledgeBaseCreateDialogData = {
      availableSources: ownedKSes.map((ks) => ({ id: ks.id, kind: ks.kind, name: ks.name, subtitle: ks.subtitle })),
      // The dialog awaits this and only closes on success — surfacing errors and keeping the inputs on
      // failure. We emit the new KB so the page prepends it and rebuilds its row set.
      onCreate: async (result: KnowledgeBaseCreateResult) => {
        let newKbId = `kb-new-${Date.now()}`;
        if (this.persist) {
          const created = await this.knowledgeService.createKnowledgeBase(
            result.name,
            result.description,
            result.sourceIds,
          );
          newKbId = created.id;
        }
        // A freshly created KB is always owned by the caller, so its identity key uses the caller namespace.
        const newKbKey = kbIdentityKey(this.knowledgeService.buildNamespaceInterface(), newKbId);
        this.kbCreated.emit({
          id: newKbId,
          kbKey: newKbKey,
          name: result.name,
          description: result.description,
          sourceCount: result.sourceIds.length,
          sourceIds: [...result.sourceIds],
          managedBy: OWNED_SENTINEL,
          lastModified: new Date(),
        });
      },
    };
    this.dialog.open(KnowledgeBaseCreateDialogComponent, {
      data,
      width: '640px',
      maxWidth: '92vw',
      autoFocus: false,
    });
  }
}
